/* Genre mix + constraints, shared by a zone's dayparts and a radio station's programmes. Preview builds on the server
   without changing what plays; Save hands the mix to the caller (a zone rebuilds that daypart's automatic playlist,
   it takes over within ~20 s). Staff see the mix read-only. */
import { For, JSX, Show, createEffect, createResource, createSignal } from 'solid-js';
import { api } from '@app/api';
import { fmtDur } from './types';
import { ErrorLine, useAction } from './ui';

type Genre = { slug: string; name: string; customer_facing: boolean };
export type GenreWeight = { genre: string; weight: number };
export type Constraints = { energy?: string; vocals?: string; explicit?: boolean; tempo_min?: number; tempo_max?: number; no_repeat_hours?: number };
type GenreStat = { weight: number; pool: number; share: number; repeat_hours: number | null };
type Preview = { stats: { tracks: number; distinct: number; hours: number; genres: Record<string, GenreStat>; empty_genres: string[] };
  tracks: { id: string; title: string; artist: string; duration_ms: number }[] };

export default function MixEditor(props: {
  mix: GenreWeight[]; constraints: Constraints; admin: boolean;
  /** POST endpoint taking { genre_mix: { default: [...] } | [...], constraints } (see previewBody) */
  previewPath: string; previewBody?: (mix: GenreWeight[], c: Constraints) => unknown;
  onSave: (mix: GenreWeight[], c: Constraints) => Promise<void>;
  saveLabel?: string; alwaysSavable?: boolean; notice?: JSX.Element;
}) {
  const [genres] = createResource(() => api<Genre[]>('/library/genres'));
  const [mix, setMix] = createSignal<GenreWeight[]>([]);
  const [cons, setCons] = createSignal<Constraints>({});
  const [preview, setPreview] = createSignal<Preview | null>(null);
  const a = useAction();
  createEffect(() => {
    setMix(props.mix.map((g) => ({ ...g })));
    setCons({ ...props.constraints });
    setPreview(null);
  });

  const facing = () => (genres() ?? []).filter((g) => g.customer_facing);
  const name = (slug: string) => facing().find((g) => g.slug === slug)?.name ?? slug;
  const total = () => mix().reduce((s, g) => s + g.weight, 0);
  const unused = () => facing().filter((g) => !mix().some((m) => m.genre === g.slug));
  const saved = () => JSON.stringify(props.mix) === JSON.stringify(mix()) && JSON.stringify(clean(props.constraints)) === JSON.stringify(clean(cons()));
  const setWeight = (i: number, w: number) => { setMix(mix().map((g, k) => (k === i ? { ...g, weight: w } : g))); setPreview(null); };
  const add = (slug: string) => { if (slug) { setMix([...mix(), { genre: slug, weight: Math.max(0, 100 - total()) }]); setPreview(null); } };
  const even = () => {
    const n = mix().length;
    setMix(mix().map((g, i) => ({ ...g, weight: Math.floor(100 / n) + (i < 100 % n ? 1 : 0) })));
    setPreview(null);
  };
  const setC = (k: keyof Constraints, v: any) => { setCons({ ...cons(), [k]: v === '' || v === undefined ? undefined : v }); setPreview(null); };
  const current = () => mix().filter((g) => g.weight > 0);

  const runPreview = () => a.run(async () => setPreview(await api<Preview>(props.previewPath, { method: 'POST',
    json: props.previewBody ? props.previewBody(current(), clean(cons())) : { genre_mix: current(), constraints: clean(cons()) } })));
  const save = () => a.run(() => props.onSave(current(), clean(cons())));

  return (
    <div class="flex flex-col gap-5">
      {props.notice}

      <div class="grid gap-6 lg:grid-cols-2">
        <div>
          <h3 class="th mb-2">Genres <span class={total() === 100 ? 'text-ink-3' : 'text-error'}>· {total()} / 100</span></h3>
          <ul class="flex flex-col gap-2">
            <For each={mix()} fallback={<li class="text-sm text-ink-3">No mix yet. Add a genre to start.</li>}>
              {(g, i) => (
                <li class="flex items-center gap-3">
                  <span class="w-28 truncate font-semibold">{name(g.genre)}</span>
                  <input type="range" class="range range-primary range-xs flex-1" min="0" max="100" step="5" value={g.weight} disabled={!props.admin}
                    aria-label={`${name(g.genre)} weight`} onInput={(e) => setWeight(i(), Number(e.currentTarget.value))} />
                  <input type="number" class="input input-xs input-bordered mono w-16" min="0" max="100" value={g.weight} disabled={!props.admin}
                    aria-label={`${name(g.genre)} weight percent`} onChange={(e) => setWeight(i(), Math.max(0, Math.min(100, Number(e.currentTarget.value) || 0)))} />
                  <Show when={props.admin}>
                    <button class="btn btn-ghost btn-xs" aria-label={`Remove ${name(g.genre)}`} onClick={() => { setMix(mix().filter((_, k) => k !== i())); setPreview(null); }}>✕</button>
                  </Show>
                </li>
              )}
            </For>
          </ul>
          <Show when={props.admin}>
            <div class="mt-3 flex flex-wrap gap-2">
              <select class="select select-sm select-bordered" aria-label="Add genre" value="" onChange={(e) => { add(e.currentTarget.value); e.currentTarget.value = ''; }}>
                <option value="">Add genre…</option>
                <For each={unused()}>{(g) => <option value={g.slug}>{g.name}</option>}</For>
              </select>
              <Show when={mix().length > 1}><button class="btn btn-ghost btn-sm" onClick={even}>Spread evenly</button></Show>
            </div>
          </Show>
        </div>

        <div>
          <h3 class="th mb-2">Constraints</h3>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
            <label for="c-energy">Energy</label>
            <select id="c-energy" class="select select-sm select-bordered" disabled={!props.admin} value={cons().energy ?? ''} onChange={(e) => setC('energy', e.currentTarget.value)}>
              <option value="">Any</option><option value="low">Calm (lowest third)</option><option value="medium">Medium (middle third)</option><option value="high">Lively (top third)</option>
            </select>
            <span>Tempo (BPM)</span>
            <div class="flex items-center gap-2">
              <input type="number" class="input input-sm input-bordered w-20" min="40" max="240" placeholder="min" aria-label="Minimum tempo" disabled={!props.admin}
                value={cons().tempo_min ?? ''} onChange={(e) => setC('tempo_min', e.currentTarget.value ? Number(e.currentTarget.value) : undefined)} />
              <span class="text-ink-3">to</span>
              <input type="number" class="input input-sm input-bordered w-20" min="40" max="240" placeholder="max" aria-label="Maximum tempo" disabled={!props.admin}
                value={cons().tempo_max ?? ''} onChange={(e) => setC('tempo_max', e.currentTarget.value ? Number(e.currentTarget.value) : undefined)} />
            </div>
            <label for="c-vocals">Vocals</label>
            <select id="c-vocals" class="select select-sm select-bordered" disabled={!props.admin} value={cons().vocals ?? ''} onChange={(e) => setC('vocals', e.currentTarget.value)}>
              <option value="">Allowed</option><option value="instrumental_only">Instrumental only</option>
            </select>
            <label for="c-explicit">Allow explicit lyrics</label>
            <input id="c-explicit" type="checkbox" class="toggle toggle-primary toggle-sm" disabled={!props.admin}
              checked={cons().explicit !== false} onChange={(e) => setC('explicit', e.currentTarget.checked ? undefined : false)} />
            <label for="c-norepeat">No repeat within</label>
            <div class="flex items-center gap-2">
              <input id="c-norepeat" type="number" class="input input-sm input-bordered w-20" min="1" max="168" placeholder="24" disabled={!props.admin}
                value={cons().no_repeat_hours ?? ''} onChange={(e) => setC('no_repeat_hours', e.currentTarget.value ? Number(e.currentTarget.value) : undefined)} />
              <span class="text-ink-3">hours</span>
            </div>
          </div>
        </div>
      </div>

      <Show when={props.admin}>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-sm btn-secondary" disabled={a.busy() || total() !== 100} onClick={runPreview}>Preview</button>
          <button class="btn btn-sm btn-primary" disabled={a.busy() || total() !== 100 || (saved() && !props.alwaysSavable)} onClick={save}>
            {a.busy() ? 'Working…' : props.saveLabel ?? 'Save mix'}</button>
          <Show when={total() !== 100 && mix().length}><span class="self-center text-sm text-error">Weights must add up to 100.</span></Show>
        </div>
      </Show>
      <ErrorLine error={a.error} />

      <Show when={preview()}>
        {(pv) => (
          <div class="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 class="th mb-2">How it works out · {pv().stats.hours} h, {pv().stats.distinct} different tracks</h3>
              <table class="table table-sm">
                <thead><tr><th class="th">Genre</th><th class="th">Target</th><th class="th">Actual</th><th class="th">Tracks</th><th class="th">Repeats</th></tr></thead>
                <tbody>
                  <For each={Object.entries(pv().stats.genres)}>
                    {([g, s]) => (
                      <tr>
                        <td>{name(g)}</td><td class="mono">{s.weight}%</td><td class="mono">{s.share}%</td><td class="mono">{s.pool}</td>
                        <td class={s.pool === 0 ? 'text-error' : s.repeat_hours !== null && s.repeat_hours < 6 ? 'text-warning' : 'text-ink-3'}>
                          {s.pool === 0 ? 'no matching tracks' : s.repeat_hours === null ? 'none' : `every ~${s.repeat_hours} h`}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <p class="mt-2 text-xs text-ink-3">Small genres repeat sooner. The catalogue is still growing; the mix is redrawn every night.</p>
            </div>
            <div>
              <h3 class="th mb-2">First tracks</h3>
              <ol class="flex max-h-80 flex-col gap-1 overflow-y-auto text-sm">
                <For each={pv().tracks}>
                  {(t, i) => <li class="flex gap-2"><span class="mono w-6 text-ink-3">{i() + 1}</span><span class="min-w-0 flex-1 truncate"><b>{t.title}</b> <span class="text-ink-3">{t.artist}</span></span><span class="mono text-ink-3">{fmtDur(t.duration_ms)}</span></li>}
                </For>
              </ol>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}

export function clean(c: Constraints): Constraints {
  return Object.fromEntries(Object.entries(c).filter(([, v]) => v !== undefined && v !== null && v !== '')) as Constraints;
}
